import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { getReadProvider } from '../utils/nftDiscovery';
import { CONTRACTS, HOOTLAND_AGENT_ABI } from '../contracts';

const TIERS = {
  0: { name: 'Agent', icon: '🤖', color: '#4A90D9', bg: 'rgba(74,144,217,0.1)', multi: '1.2x', rate: 12 },
  1: { name: 'Sentinel', icon: '⚔️', color: '#9B59B6', bg: 'rgba(155,89,182,0.1)', multi: '1.5x', rate: 15 },
  2: { name: 'Overseer', icon: '👁️', color: '#E74C3C', bg: 'rgba(231,76,60,0.1)', multi: '2.0x', rate: 20 },
  3: { name: 'Elder', icon: '🔮', color: '#F39C12', bg: 'rgba(243,156,18,0.1)', multi: '3.0x', rate: 30 },
};

export default function Collection() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, byTier: {} });
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchAllAgents();
  }, []);

  const fetchAllAgents = async () => {
    try {
      const provider = getReadProvider();
      const contract = new ethers.Contract(CONTRACTS.HootlandAgent, HOOTLAND_AGENT_ABI, provider);
      
      // Get total supply from contract
      const supply = await contract.genesisSupply();
      
      const allAgents = [];
      const tierCounts = { 0: 0, 1: 0, 2: 0, 3: 0 };
      
      // Check each token ID from 1 to supply
      for (let i = 1; i <= supply; i++) {
        try {
          const agentInfo = await contract.getAgentInfo(i);
          const owner = await contract.ownerOf(i);
          
          const tier = Number(agentInfo.tier);
          tierCounts[tier]++;
          
          allAgents.push({
            tokenId: i.toString(),
            tier: tier,
            owner: owner,
            ownerShort: `${owner.slice(0, 6)}...${owner.slice(-4)}`,
            multiplier: Number(agentInfo.multiplier),
            x: Number(agentInfo.x),
            y: Number(agentInfo.y),
            earned: ethers.formatUnits(agentInfo.totalEarned || 0, 18),
          });
        } catch (e) {
          // Skip if agent doesn't exist
        }
      }
      
      setAgents(allAgents.reverse()); // Newest first
      setStats({ total: supply, byTier: tierCounts });
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      setLoading(false);
    }
  };

  const filteredAgents = filter === 'all' 
    ? agents 
    : agents.filter(a => a.tier === parseInt(filter));

  if (loading) {
    return (
      <div style={s.container}>
        <div style={s.loading}>🦉 Loading Hootland Collection...</div>
      </div>
    );
  }

  return (
    <div style={s.container}>
      <header style={s.header}>
        <h1 style={s.title}>🦉 Hootland Collection</h1>
        <p style={s.subtitle}>All {stats.total} agents in the ecosystem</p>
      </header>

      {/* Stats */}
      <div style={s.statsGrid}>
        <div style={s.statCard}>
          <div style={s.statNumber}>{stats.total}</div>
          <div style={s.statLabel}>Total Agents</div>
        </div>
        <div style={{...s.statCard, borderColor: TIERS[0].color}}>
          <div style={{...s.statNumber, color: TIERS[0].color}}>{stats.byTier[0] || 0}</div>
          <div style={s.statLabel}>Agents (50%)</div>
        </div>
        <div style={{...s.statCard, borderColor: TIERS[1].color}}>
          <div style={{...s.statNumber, color: TIERS[1].color}}>{stats.byTier[1] || 0}</div>
          <div style={s.statLabel}>Sentinels (30%)</div>
        </div>
        <div style={{...s.statCard, borderColor: TIERS[2].color}}>
          <div style={{...s.statNumber, color: TIERS[2].color}}>{stats.byTier[2] || 0}</div>
          <div style={s.statLabel}>Overseers (15%)</div>
        </div>
        <div style={{...s.statCard, borderColor: TIERS[3].color}}>
          <div style={{...s.statNumber, color: TIERS[3].color}}>{stats.byTier[3] || 0}</div>
          <div style={s.statLabel}>Elders (5%)</div>
        </div>
      </div>

      {/* Filters */}
      <div style={s.filters}>
        <button style={{...s.filterBtn, ...(filter === 'all' ? s.filterActive : {})}} onClick={() => setFilter('all')}>
          All
        </button>
        {Object.entries(TIERS).map(([id, tier]) => (
          <button key={id} style={{...s.filterBtn, ...(filter === id ? {...s.filterActive, borderColor: tier.color} : {})}} onClick={() => setFilter(id)}>
            {tier.icon} {tier.name}
          </button>
        ))}
      </div>

      {/* Agent Grid */}
      <div style={s.grid}>
        {filteredAgents.map(agent => {
          const tier = TIERS[agent.tier] || TIERS[0];
          return (
            <div key={agent.tokenId} style={{...s.card, borderColor: tier.color + '40', background: tier.bg}}>
              <div style={s.cardHeader}>
                <span style={s.tokenId}>#{agent.tokenId}</span>
                <span style={{...s.tierBadge, color: tier.color, borderColor: tier.color}}>
                  {tier.icon} {tier.name}
                </span>
              </div>
              
              <div style={s.cardImage}>
                <span style={{fontSize: 64}}>{tier.icon}</span>
              </div>
              
              <div style={s.cardStats}>
                <div style={s.stat}>
                  <span style={s.statLabel}>Multiplier</span>
                  <span style={{...s.statValue, color: tier.color}}>{tier.multi}</span>
                </div>
                <div style={s.stat}>
                  <span style={s.statLabel}>Earning Rate</span>
                  <span style={s.statValue}>{tier.rate} OWL/hr</span>
                </div>
                <div style={s.stat}>
                  <span style={s.statLabel}>Position</span>
                  <span style={s.statValue}>{agent.x}, {agent.y}</span>
                </div>
                <div style={s.stat}>
                  <span style={s.statLabel}>Total Earned</span>
                  <span style={s.statValue}>{parseFloat(agent.earned).toFixed(2)} OWL</span>
                </div>
              </div>
              
              <div style={s.owner}>
                <span style={s.ownerLabel}>Owner:</span>
                <a 
                  href={`https://basescan.org/address/${agent.owner}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={s.ownerLink}
                >
                  {agent.ownerShort}
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {filteredAgents.length === 0 && (
        <div style={s.empty}>
          <span style={{fontSize: 48}}>🦉</span>
          <p>No agents found in this category yet.</p>
        </div>
      )}
    </div>
  );
}

// Styles
const s = {
  container: {
    maxWidth: 1400,
    margin: '0 auto',
    padding: '40px 24px',
    minHeight: '100vh',
    background: '#07070f',
    color: '#e2e8f0',
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
  },
  loading: {
    textAlign: 'center',
    padding: 100,
    fontSize: 18,
    color: '#7eb8f7',
  },
  header: {
    textAlign: 'center',
    marginBottom: 48,
  },
  title: {
    fontSize: 42,
    fontWeight: 700,
    color: '#7eb8f7',
    marginBottom: 12,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: 16,
    color: '#64748b',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 16,
    marginBottom: 32,
  },
  statCard: {
    background: 'rgba(126,184,247,0.06)',
    border: '1px solid rgba(126,184,247,0.15)',
    borderRadius: 12,
    padding: 20,
    textAlign: 'center',
  },
  statNumber: {
    fontSize: 32,
    fontWeight: 700,
    color: '#7eb8f7',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  filters: {
    display: 'flex',
    gap: 12,
    marginBottom: 32,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  filterBtn: {
    background: 'rgba(126,184,247,0.06)',
    border: '1px solid rgba(126,184,247,0.15)',
    borderRadius: 8,
    padding: '10px 20px',
    color: '#94a3b8',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  filterActive: {
    background: 'rgba(126,184,247,0.15)',
    borderColor: '#7eb8f7',
    color: '#7eb8f7',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 20,
  },
  card: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 16,
    padding: 20,
    transition: 'transform 0.2s, box-shadow 0.2s',
    ':hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  tokenId: {
    fontSize: 14,
    fontWeight: 700,
    color: '#64748b',
  },
  tierBadge: {
    padding: '4px 12px',
    borderRadius: 20,
    border: '1px solid',
    fontSize: 12,
    fontWeight: 600,
  },
  cardImage: {
    height: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.2)',
    borderRadius: 12,
    marginBottom: 16,
  },
  cardStats: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    marginBottom: 16,
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  statValue: {
    fontSize: 14,
    fontWeight: 600,
    color: '#e2e8f0',
  },
  owner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingTop: 12,
    borderTop: '1px solid rgba(255,255,255,0.06)',
  },
  ownerLabel: {
    fontSize: 12,
    color: '#64748b',
  },
  ownerLink: {
    color: '#7eb8f7',
    fontSize: 12,
    textDecoration: 'none',
    fontFamily: 'monospace',
  },
  empty: {
    textAlign: 'center',
    padding: 60,
    color: '#64748b',
  },
};
